import { SortDirections, wafflesKeyColumnName } from "@app/shared/waffles";
import { JSX } from "react";

export interface AIChatTableChartData {
  [wafflesKeyColumnName]: string;

  [key: string]: string | JSX.Element;
}

const DEFAULT_PAGE_SIZE = 25;

interface AIChatTableChartState {
  modalOpen: boolean;
  isModalReady: boolean;
  paginatedData: AIChatTableChartData[];
  paginatedHeaders: string[];
  paginationPage: number;
  paginationPageSize: number;
  paginationTotalCount: number;
  isPaginationLoading: boolean;
  isDownloading: boolean;
  sortColumn: string | null;
  sortDirection: SortDirections;
  columnMap: Record<string, string>;
  resetToFirstPage: boolean;
}

type AIChatTableChartAction =
  | { type: 'TOGGLE_MODAL'; payload: { isPaginationEnabled: boolean } }
  | { type: 'SET_MODAL_READY'; payload: boolean }
  | { type: 'PREPARE_FETCH'; payload: { page: number; pageSize: number; sortColumn: string | null; sortDirection: SortDirections } }
  | { type: 'FETCH_SUCCESS'; payload: { totalCount: number; headers: string[]; data: AIChatTableChartData[]; columnMap: Record<string, string> } }
  | { type: 'FETCH_FAILURE' }
  | { type: 'CHANGE_PAGE'; payload: number }
  | { type: 'SET_DOWNLOADING'; payload: boolean };

export const aiChatTableChartInitialState: AIChatTableChartState = {
  columnMap: {},
  isDownloading: false,
  isModalReady: false,
  isPaginationLoading: false,
  modalOpen: false,
  paginatedData: [],
  paginatedHeaders: [],
  paginationPage: 1,
  paginationPageSize: DEFAULT_PAGE_SIZE,
  paginationTotalCount: 0,
  resetToFirstPage: false,
  sortColumn: null,
  sortDirection: SortDirections.None,
};

export function aiChatTableChartReducer(
  state: AIChatTableChartState,
  action: AIChatTableChartAction
): AIChatTableChartState {
  switch (action.type) {
    case 'TOGGLE_MODAL': {
      const nextOpen = !state.modalOpen;
      if (nextOpen && action.payload.isPaginationEnabled) {
        return {
          ...state,
          columnMap: {},
          isModalReady: false,
          modalOpen: true,
          paginatedData: [],
          paginatedHeaders: [],
          paginationPage: 1,
          paginationPageSize: DEFAULT_PAGE_SIZE,
          resetToFirstPage: false,
          sortColumn: null,
          sortDirection: SortDirections.None,
        };
      }
      return { ...state, isModalReady: false, modalOpen: nextOpen };
    }
    case 'SET_MODAL_READY':
      return { ...state, isModalReady: action.payload };
    case 'PREPARE_FETCH': {
      const sortChanged = action.payload.sortColumn !== state.sortColumn
        || action.payload.sortDirection !== state.sortDirection;
      return {
        ...state,
        isPaginationLoading: true,
        paginationPage: action.payload.page,
        paginationPageSize: action.payload.pageSize,
        resetToFirstPage: sortChanged,
        sortColumn: action.payload.sortColumn,
        sortDirection: action.payload.sortDirection,
      };
    }
    case 'FETCH_SUCCESS':
      return {
        ...state,
        columnMap: Object.keys(action.payload.columnMap).length > 0
          ? action.payload.columnMap
          : state.columnMap,
        isPaginationLoading: false,
        paginatedData: action.payload.data,
        paginatedHeaders: action.payload.headers,
        paginationTotalCount: action.payload.totalCount,
      };
    case 'FETCH_FAILURE':
      return { ...state, isPaginationLoading: false };
    case 'CHANGE_PAGE':
      if (state.resetToFirstPage) {
        return { ...state, resetToFirstPage: false };
      }
      return { ...state, paginationPage: action.payload };
    case 'SET_DOWNLOADING':
      return { ...state, isDownloading: action.payload };
    default:
      return state;
  }
}
